import { RefObject, useEffect, useRef } from 'react';

const INITIAL_DELAY = 200;
const RETRY_INTERVAL = 100;
const OBSERVER_MAX_WAIT = 5000;
const MAX_ATTEMPTS = 30;
const STABLE_TICKS = 3;

interface UseAutoFocusFieldParams<T extends HTMLElement> {
    enabled?: boolean;
    loading?: boolean;
    initialDelay?: number;
    resolveTarget: (container: T) => HTMLElement | null;
}

// v7 overlays move focus to their own focus-trap after opening, so focus must be re-asserted until
// it sticks. `resolveTarget` must be a stable reference declared outside the component.
export const useAutoFocusField = <T extends HTMLElement>({
    enabled = true,
    loading = false,
    initialDelay,
    resolveTarget,
}: UseAutoFocusFieldParams<T>): RefObject<T> => {
    const ref = useRef<T>(null);

    useEffect(() => {
        if (!enabled || loading) {
            return undefined;
        }

        let stopped = false;
        let attempts = 0;
        let stableTicks = 0;
        let focusTimer: ReturnType<typeof setTimeout> | undefined;
        let observerTimeout: ReturnType<typeof setTimeout> | undefined;
        let observer: MutationObserver | undefined;

        const cleanup = () => {
            stopped = true;
            clearTimeout(startTimer);
            if (focusTimer) {
                clearTimeout(focusTimer);
            }
            if (observerTimeout) {
                clearTimeout(observerTimeout);
            }
            observer?.disconnect();
        };

        const resolve = (): HTMLElement | null => (ref.current ? resolveTarget(ref.current) : null);

        const stableLoop = (target: HTMLElement) => {
            if (stopped) {
                return;
            }
            attempts += 1;
            if (document.activeElement === target) {
                stableTicks += 1;
            } else {
                target.focus();
                stableTicks = 0;
            }
            if (stableTicks < STABLE_TICKS && attempts < MAX_ATTEMPTS) {
                focusTimer = setTimeout(() => stableLoop(target), RETRY_INTERVAL);
            } else {
                cleanup();
            }
        };

        const begin = (): boolean => {
            const target = resolve();
            if (!target) {
                return false;
            }
            observer?.disconnect();
            if (observerTimeout) {
                clearTimeout(observerTimeout);
                observerTimeout = undefined;
            }
            attempts = 0;
            stableTicks = 0;
            stableLoop(target);
            return true;
        };

        const startTimer = setTimeout(() => {
            if (begin()) {
                return;
            }
            // Fields can render late and owc reflects `autofocus`/`disabled` after insertion, so
            // observe attributes too — a childList-only observer would miss them.
            observer = new MutationObserver(() => {
                if (!stopped) {
                    begin();
                }
            });
            observer.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['autofocus', 'disabled'],
            });
            observerTimeout = setTimeout(cleanup, OBSERVER_MAX_WAIT);
        }, initialDelay ?? INITIAL_DELAY);

        return cleanup;
    }, [enabled, loading, resolveTarget, initialDelay]);

    return ref;
};
